import type {
  BaseRecord,
  CustomParams,
  CustomResponse,
  DataProvider,
  GetListParams,
  GetListResponse
} from '@refinedev/core';

import type { DataElement } from '@/types';
import { toDataElement } from '@/utils/mapper';
import type { ListMetaData } from '@/utils/types';

import type { DataProviderEnhancer } from '../types';

export const withCustomFieldEnhancer: DataProviderEnhancer = (
  provider: DataProvider
) => {
  return {
    ...provider,

    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams
    ): Promise<GetListResponse<TData>> => {
      const response = await provider.getList<TData>(params);
      injectCustomFields(response.data);
      return response;
    },

    custom: async <
      TData extends BaseRecord = BaseRecord,
      TQuery = unknown,
      TPayload = unknown
    >(
      params: CustomParams<TQuery, TPayload>
    ): Promise<CustomResponse<TData>> => {
      const response = await provider.custom!<TData>(params);
      injectCustomFields(response.data);
      return response;
    }
  };
};

function isListData(
  data: unknown
): data is { metadata: ListMetaData; customFields?: DataElement[] } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'metadata' in data &&
    (data as { metadata: unknown }).metadata !== null
  );
}

function injectCustomFields(data: unknown): void {
  if (!isListData(data)) return;

  const metadata = data.metadata;
  if (!metadata.custom_fields) return;

  try {
    data.customFields = metadata.custom_fields
      .map(toDataElement)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  } catch (err) {
    console.warn('Failed to inject custom fields', err);
  }
}
